// ./src/components/features/CreateEventWizard/steps/ChannelsStep.tsx
//
// "Where will you promote it?" — shown when the event has Marketing channels on (ROADMAP A2b #62; owner, 2026-09-29:
// "if the host chose marketing channels and none exist yet, add a step to create them").
//
// ⓘ SHOWN WHENEVER THE FEATURE IS ON, not only while the account has none: channels are ACCOUNT-wide, so a host with
//   some already lands on a short step that lists them with this event's tracked links, and can add the one this
//   event needs. Hiding it would need a read before the step list can be drawn, and a step that appears late moves
//   the host's Next button.
//
// Everything is the event Marketing channels page's own pieces — `EventMarketingChannelsBoard` (this event's
// `?ch=` link per channel, with Copy + QR), `MarketingChannelEditDrawer`, `ChannelLinkQrDrawer` — under a provider
// scoped by the event, so a co-host's create carries its auth context.
// ⚠ The links are real now but open nothing until the event is PUBLISHED — the finish screen offers that; this step
//   says so rather than letting a host send a link that 404s.

import React, { useMemo, useState } from 'react';
import { Stack, Group, Text, Button, Alert } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { MarketingChannelsProvider, useMarketingChannelsContext } from '@/contexts';
import { useEvent } from '@/hooks';
import { MarketingChannel, WHOLE_SET } from '@/utils/api';
import { buildEventPublicUrl } from '@/utils';
import { AppIcon, ErrorAlert } from '@/components/generic';
import { EventMarketingChannelsBoard, MarketingChannelEditDrawer, ChannelLinkQrDrawer } from '@/components/features/MarketingChannels';

import { useEventWizard } from '../WizardContext';

/** The places most hosts promote an event — each opens the real drawer with the name filled in. */
const CHANNEL_STARTERS = ['Instagram', 'Facebook', 'Newsletter', 'Flyer / poster', 'LinkedIn'];

const ChannelsStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'marketingChannels', 'common']);
  const { saving } = useEventWizard();
  const { marketingChannels, isLoaded, lastError, clearError } = useMarketingChannelsContext();
  const { event } = useEvent(eventId);
  // ⓘ No plan lock: marketing channels are on every plan since 2026-10-22 (owner).

  const [drawer, setDrawer] = useState<{ initialName: string | null } | null>(null);
  const [qr, setQr] = useState<{ channel: MarketingChannel; url: string } | null>(null);

  const eventUrlBase = useMemo(() => buildEventPublicUrl(event, { fallbackId: eventId }) ?? '', [event, eventId]);
  const starters = CHANNEL_STARTERS.filter(
    (name) => !marketingChannels.some((c) => (c.name ?? '').trim().toLowerCase() === name.toLowerCase()),
  );

  const openNew = (initialName: string | null) => {
    setDrawer({ initialName });
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.channels.intro', {
          defaultValue:
            'Add each place you will promote this event. Every one gets its own tracked link and QR code, so you can see which brings guests. Channels are shared across all your events.',
        })}
      </Text>

      {event && event.eventStatus !== 'published' ? (
        <Alert variant="light" color="gray" icon={<AppIcon iconName="InfoCircle" size={18} />}>
          <Text size="md">
            {t('events:wizard.channels.draftNote', {
              defaultValue: 'The links open your event once it is published — you can publish on the last step.',
            })}
          </Text>
        </Alert>
      ) : null}

      <ErrorAlert
        error={lastError}
        title={t('marketingChannels:errors.loadTitle', { defaultValue: "Couldn't complete that" })}
        page="Create event — marketing channels"
        operation="marketingChannelList"
        onDismiss={clearError}
      />

      <Group justify="space-between" wrap="nowrap">
        <Text fw={600} size="md">
          {t('events:wizard.channels.title', { defaultValue: 'Your channels' })}
        </Text>
        <Button
          variant="light"
          size="md"
          leftSection={<AppIcon iconName="Plus" size={16} />}
          disabled={saving}
          onClick={() => openNew(null)}
        >
          {t('events:wizard.channels.add', { defaultValue: 'Add a channel' })}
        </Button>
      </Group>

      {isLoaded && starters.length > 0 ? (
        <Group gap="xs">
          {starters.map((name) => (
            <Button
              key={name}
              variant="default"
              size="md"
              leftSection={<AppIcon iconName="Plus" size={16} />}
              disabled={saving}
              onClick={() => openNew(name)}
            >
              {name}
            </Button>
          ))}
        </Group>
      ) : null}

      <EventMarketingChannelsBoard
        channels={marketingChannels}
        eventUrlBase={eventUrlBase}
        isLoaded={isLoaded}
        onAddChannel={() => openNew(null)}
        onShowQr={(channel, url) => setQr({ channel, url })}
      />

      <MarketingChannelEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={null}
        initialName={drawer?.initialName ?? null}
        onCreated={() => setDrawer(null)}
      />

      {/* ⓘ Local state, not a URL stage: it opens inside the wizard's own step (#130). */}
      <ChannelLinkQrDrawer
        opened={!!qr}
        onClose={() => setQr(null)}
        url={qr?.url ?? null}
        channelName={qr?.channel.name}
        code={qr?.channel.code}
      />
    </Stack>
  );
};

export const ChannelsStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <MarketingChannelsProvider pageSize={WHOLE_SET} eventId={createdEventId}>
      <ChannelsStepBody eventId={createdEventId} />
    </MarketingChannelsProvider>
  );
};
